import { Box, BoxProps, styled } from '@mui/material';

// Tech icons styles
export const TechIcons = styled(Box)(({ theme }) => ({
  fontSize: '4.5em',
  margin: '15px',
  padding: '10px',
  opacity: 0.93,
  border: '1.7px solid rgba(200, 137, 230, 0.637)',
  verticalAlign: 'middle',
  textAlign: 'center',
  borderRadius: '5px',
  display: 'table',
  boxShadow: '4px 5px 4px 3px rgba(89, 4, 168, 0.137)',
  overflow: 'hidden',
  transition: 'all 0.4s ease 0s',
  [theme.breakpoints.down('md')]: {
    margin: '10px',
  },
  '&:hover': {
    transform: 'scale(1.05)',
    overflow: 'hidden',
    border: '2.2px solid rgba(197, 115, 230, 0.883)',
  },
}));

export const TechIconImages = styled(Box)({
  padding: '20px',
  lineHeight: '1.6',
});

// Tech stack icons
export const TechIcon = styled(Box, {
  shouldForwardProp: prop => prop !== 'isHovered',
})<BoxProps & { isHovered: boolean }>(({ theme, isHovered }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: 'rgba(199, 112, 240, 0.1)',
  border: '1px solid rgba(199, 112, 240, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(199, 112, 240, 0.3)',
  },
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  height: '120px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '& .icon-container': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    opacity: isHovered ? 0 : 1,
    transform: isHovered
      ? 'translate(-50%, -50%) scale(0.8)'
      : 'translate(-50%, -50%) scale(1)',
  },
  '& .icon': {
    fontSize: '2.5rem',
    color: '#c770f0',
    transition: 'all 0.3s ease',
    animation: isHovered ? 'iconGlitchOut 1.2s ease-in-out' : 'none',
  },
  '& .skill-name': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    fontWeight: 'bold',
    color: '#fff',
    fontSize: '1.1rem',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    opacity: isHovered ? 1 : 0,
    transform: isHovered
      ? 'translate(-50%, -50%) scale(1)'
      : 'translate(-50%, -50%) scale(0.8)',
    animation: isHovered ? 'textGlitchIn 1.2s ease-in-out 0.6s' : 'none',
  },
  '@keyframes iconGlitchOut': {
    '0%': {
      transform: 'translate(0)',
      opacity: 1,
      filter: 'hue-rotate(0deg)',
    },
    '5%': {
      transform: 'translate(-5px, 2px)',
      opacity: 0.9,
      filter: 'hue-rotate(45deg) brightness(1.2)',
    },
    '10%': {
      transform: 'translate(4px, -3px)',
      opacity: 0.8,
      filter: 'hue-rotate(90deg) brightness(1.4)',
    },
    '15%': {
      transform: 'translate(-3px, -4px)',
      opacity: 0.7,
      filter: 'hue-rotate(135deg) brightness(1.6)',
    },
    '20%': {
      transform: 'translate(6px, 1px)',
      opacity: 0.6,
      filter: 'hue-rotate(180deg) brightness(1.8)',
    },
    '25%': {
      transform: 'translate(-2px, 5px)',
      opacity: 0.5,
      filter: 'hue-rotate(225deg) brightness(2.0)',
    },
    '30%': {
      transform: 'translate(3px, -2px)',
      opacity: 0.4,
      filter: 'hue-rotate(270deg) brightness(1.8)',
    },
    '35%': {
      transform: 'translate(-4px, -3px)',
      opacity: 0.3,
      filter: 'hue-rotate(315deg) brightness(1.6)',
    },
    '40%': {
      transform: 'translate(2px, 4px)',
      opacity: 0.2,
      filter: 'hue-rotate(360deg) brightness(1.4)',
    },
    '45%': {
      transform: 'translate(-1px, -1px)',
      opacity: 0.1,
      filter: 'hue-rotate(45deg) brightness(1.2)',
    },
    '50%': {
      transform: 'translate(3px, 2px)',
      opacity: 0.05,
      filter: 'hue-rotate(90deg) brightness(1.0)',
    },
    '55%': {
      transform: 'translate(-2px, -2px)',
      opacity: 0.02,
      filter: 'hue-rotate(135deg) brightness(0.8)',
    },
    '60%': {
      transform: 'translate(1px, 1px)',
      opacity: 0.01,
      filter: 'hue-rotate(180deg) brightness(0.6)',
    },
    '100%': {
      transform: 'translate(0)',
      opacity: 0,
      filter: 'hue-rotate(0deg) brightness(0)',
    },
  },
  '@keyframes textGlitchIn': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(0.5)',
      opacity: 0,
      filter: 'hue-rotate(0deg) brightness(0)',
    },
    '10%': {
      transform: 'translate(-50%, -50%) scale(0.7)',
      opacity: 0.3,
      filter: 'hue-rotate(45deg) brightness(1.2)',
    },
    '20%': {
      transform: 'translate(-50%, -50%) scale(0.9)',
      opacity: 0.5,
      filter: 'hue-rotate(90deg) brightness(1.4)',
    },
    '30%': {
      transform: 'translate(-50%, -50%) scale(1.2)',
      opacity: 0.7,
      filter: 'hue-rotate(135deg) brightness(1.6)',
    },
    '40%': {
      transform: 'translate(-50%, -50%) scale(0.8)',
      opacity: 0.8,
      filter: 'hue-rotate(180deg) brightness(1.8)',
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.1)',
      opacity: 0.9,
      filter: 'hue-rotate(225deg) brightness(2.0)',
    },
    '60%': {
      transform: 'translate(-50%, -50%) scale(0.95)',
      opacity: 0.95,
      filter: 'hue-rotate(270deg) brightness(1.8)',
    },
    '70%': {
      transform: 'translate(-50%, -50%) scale(1.05)',
      opacity: 0.98,
      filter: 'hue-rotate(315deg) brightness(1.6)',
    },
    '80%': {
      transform: 'translate(-50%, -50%) scale(0.98)',
      opacity: 0.99,
      filter: 'hue-rotate(360deg) brightness(1.4)',
    },
    '90%': {
      transform: 'translate(-50%, -50%) scale(1.02)',
      opacity: 1,
      filter: 'hue-rotate(45deg) brightness(1.2)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(1)',
      opacity: 1,
      filter: 'hue-rotate(0deg) brightness(1)',
    },
  },
}));
